/**
 * Org branding — runtime CSS-variable override.
 *
 * Per Populus/doctrine/design-system.md § Color — System vs Brand tokens:
 *   - Brand tokens (--brand-primary, --brand-secondary, --brand-accent,
 *     --brand-primary-bg, --brand-on-primary) are org-configurable.
 *   - At boot, fetch the current org's brand colors and apply via
 *     document.documentElement.style.setProperty(). Defaults stay in tokens.css;
 *     overrides only when org has non-null values configured.
 *
 * Fetch source: GET /api/current-user returns the user envelope + org branding
 * (extended in Phase 11 Block 6 PR 3b backend wire commit).
 *
 * Pre-PR-3b: this util uses default Holdings palette only (no API fetch).
 * Post-PR-3b: real org branding overrides applied at first render.
 *
 * Phase 11 Block 6 PR 3a (2026-05-12).
 */

/**
 * Brand colors fetched from CoreOS org settings. All fields nullable —
 * orgs that haven't configured a color fall back to Holdings defaults.
 */
export interface OrgBranding {
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;
}

/**
 * Apply an org's brand colors to the document root by overriding the
 * --brand-* CSS custom properties. Null values are skipped — defaults
 * from tokens.css remain in effect.
 */
export function applyOrgBranding(branding: OrgBranding): void {
  const root = document.documentElement;

  if (branding.brand_primary_color) {
    root.style.setProperty('--brand-primary', branding.brand_primary_color);
    // Future enhancement: auto-derive --brand-on-primary (white vs black based on
    // primary's L* value) and --brand-primary-bg (92% L* tint of primary).
    // For now, super admin sets primary; on-primary stays white; bg tints stay default.
  }
  if (branding.brand_secondary_color) {
    root.style.setProperty('--brand-secondary', branding.brand_secondary_color);
  }
  if (branding.brand_accent_color) {
    root.style.setProperty('--brand-accent', branding.brand_accent_color);
  }
}

/**
 * Reset all brand tokens to defaults. Used by the design-system catalog
 * route to preview the override mechanism without persisting.
 */
export function resetBranding(): void {
  const root = document.documentElement;
  root.style.removeProperty('--brand-primary');
  root.style.removeProperty('--brand-secondary');
  root.style.removeProperty('--brand-accent');
  root.style.removeProperty('--brand-primary-bg');
  root.style.removeProperty('--brand-on-primary');
}

/**
 * Read the current org branding from a /api/current-user response.
 * The response shape includes org-level brand fields when the backend wire
 * lands in Phase 11 Block 6 PR 3b.
 */
export function extractOrgBranding(currentUserResponse: unknown): OrgBranding {
  // Defensive: handle pre-wire responses that don't include brand fields
  const obj = currentUserResponse as Record<string, unknown> | null;
  if (!obj) return { brand_primary_color: null, brand_secondary_color: null, brand_accent_color: null };
  return {
    brand_primary_color: (obj.brand_primary_color as string | null) ?? null,
    brand_secondary_color: (obj.brand_secondary_color as string | null) ?? null,
    brand_accent_color: (obj.brand_accent_color as string | null) ?? null,
  };
}
